import React, { useEffect } from "react";
import { motion } from "framer-motion";
import vrj from "../assets/vrj2.jpg";
import { Helmet } from "react-helmet-async";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-dreamy-cream pt-32 pb-20 overflow-hidden">
      <Helmet>
        {/* Standard SEO */}
        <title>
          About Hasanka | Professional Wedding & Portrait Photographer Sri Lanka
        </title>
        <meta
          name="description"
          content="Meet Hasanka, a specialist in vintage-aesthetic and natural light photography. Providing professional wedding, lifestyle, and nature photography services in Kurunegala, Colombo, and across Sri Lanka."
        />
        <meta
          name="keywords"
          content="Hasanka photographer, Kurunegala wedding photographers, Colombo portrait photography, Sri Lanka nature photographer, vintage aesthetic photography LK, cinematic stills Sri Lanka"
        />

        {/* Social Meta */}
        <meta
          property="og:title"
          content="Behind the Lens | Hasanka - Photographer in Sri Lanka"
        />
        <meta
          property="og:description"
          content="Discover the story and philosophy behind the dreamy, natural light photography of Hasanka."
        />
        <meta property="og:url" content="https://stillsbyhasanka.com/about" />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content={vrj} />

        {/* Structured Data (Schema.org) for Local SEO */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Person",
              "name": "Hasanka",
              "jobTitle": "Professional Photographer",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Kurunegala",
                "addressRegion": "North Western Province",
                "addressCountry": "LK"
              },
              "url": "https://stillsbyhasanka.com/about",
              "description": "Specialist in natural light and vintage aesthetic photography based in Sri Lanka.",
              "brand": {
                "@type": "Brand",
                "name": "StillsByHasanka"
              },
              "knowsAbout": ["Wedding Photography", "Portrait Photography", "Nature Photography", "Natural Light Photography"]
            }
          `}
        </script>
      </Helmet>

      <div className="container mx-auto px-6 md:px-20">
        {/* ... Rest of your existing sections (Section 1, 2, 3) ... */}

        {/* SECTION 1: THE STORY (Hero-style) */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="relative z-10 w-full max-w-md aspect-[4/5] rounded-t-full overflow-hidden border-[12px] border-white shadow-2xl">
              <img
                src={vrj}
                alt="Hasanka - Photographer based in Kurunegala Sri Lanka"
                className="w-full h-full object-cover sepia-[10%] brightness-105"
              />
            </div>
            <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-dreamy-gold/20 blur-[80px] rounded-full -z-0" />
            <div className="absolute top-1/2 -left-8 w-32 h-32 border border-dreamy-gold/30 rounded-full -z-0" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="font-serif text-5xl md:text-7xl text-dreamy-earth leading-tight">
              Behind the <span className="italic text-dreamy-gold">Lens</span>
            </h2>
            <div className="space-y-6 font-sans text-dreamy-earth/80 text-lg leading-relaxed">
              <p>
                Based in the heart of{" "}
                <span className="text-dreamy-earth font-medium">Sri Lanka</span>
                , I’ve spent the last decade chasing the sun. My journey into
                photography started not with a camera, but with a fascination
                for how light changes the mood of a room.
              </p>
              <p>
                I don’t just take "pictures." I capture the quiet, unscripted
                moments—the way the wind catches a dress, the soft glow on a
                face at golden hour, and the stillness of nature. My style is
                rooted in a{" "}
                <span className="italic">vintage, dreamy aesthetic</span> that
                feels timeless.
              </p>
            </div>
          </motion.div>
        </section>

        {/* SECTION 2: THE PHILOSOPHY (Nature Friendly Vibe) */}
        <section className="bg-[#F9F4EE] -mx-6 md:-mx-20 px-6 md:px-20 py-24 my-24 rounded-[100px_0_100px_0]">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <h3 className="font-serif text-4xl text-dreamy-earth">
              The Philosophy
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                {
                  title: "Natural Light",
                  desc: "No harsh flashes. Only the soft, organic glow of the sun.",
                },
                {
                  title: "Authenticity",
                  desc: "Minimal posing. I focus on real emotions and raw movements.",
                },
                {
                  title: "Nature First",
                  desc: "Integrating the beauty of the Sri Lankan landscape into every frame.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.2 }}
                  className="space-y-4"
                >
                  <div className="w-10 h-[1px] bg-dreamy-gold mx-auto" />
                  <h4 className="font-serif italic text-2xl text-dreamy-earth/90">
                    {item.title}
                  </h4>
                  <p className="font-sans text-sm text-dreamy-earth/60 tracking-wide leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3: THE GEAR / FUN FACT */}
        <section className="max-w-2xl mx-auto text-center py-10">
          <p className="font-sans text-[10px] tracking-[0.5em] uppercase text-dreamy-earth/40 mb-4">
            The Toolset
          </p>
          <p className="font-serif text-2xl text-dreamy-earth/70 italic">
            Shooting with Sony Alpha systems & Vintage Prime Lenses to achieve
            that signature soft bokeh.
          </p>
        </section>
      </div>
    </main>
  );
};

export default AboutPage;
